import React, { useState, useMemo } from 'react';

// Detect OS
const detectOS = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'windows'; // Default fallback
  }
  
  const platform = navigator.platform || navigator.userAgentData?.platform || '';
  const userAgent = navigator.userAgent || '';
  
  // Check for Mac
  if (platform.toUpperCase().indexOf('MAC') >= 0 || userAgent.toUpperCase().indexOf('MAC') >= 0) {
    return 'mac';
  }
  
  // Check for Windows
  if (platform.toUpperCase().indexOf('WIN') >= 0 || userAgent.toUpperCase().indexOf('WIN') >= 0) {
    return 'windows';
  }
  
  // Default to Windows
  return 'windows';
};

// Get instructions based on OS
const getUmlautInstructions = (os) => {
  if (os === 'mac') {
    return {
      ä: 'Hold Option + U, then type A',
      ö: 'Hold Option + U, then type O',
      ü: 'Hold Option + U, then type U',
      Ä: 'Hold Option + U, then type A (Shift for capital)',
      Ö: 'Hold Option + U, then type O (Shift for capital)',
      Ü: 'Hold Option + U, then type U (Shift for capital)',
      ß: 'Hold Option + S',
    };
  } else {
    // Windows
    return {
      ä: 'Hold Alt, type 0228 on numpad',
      ö: 'Hold Alt, type 0246 on numpad',
      ü: 'Hold Alt, type 0252 on numpad',
      Ä: 'Hold Alt, type 0196 on numpad',
      Ö: 'Hold Alt, type 0214 on numpad',
      Ü: 'Hold Alt, type 0220 on numpad',
      ß: 'Hold Alt, type 0223 on numpad',
    };
  }
};

const UmlautHelper = ({ showTooltip = true, langCode = null }) => {
  const [isHovered, setIsHovered] = useState(false);
  const os = useMemo(() => detectOS(), []);
  const instructions = useMemo(() => getUmlautInstructions(os), [os]);

  // Only show umlaut helper for German language (lang-code: 'de')
  if (langCode !== 'de') {
    return null;
  }

  return (
    <div
      className="relative inline-block ml-2"
      onMouseEnter={showTooltip ? () => setIsHovered(true) : undefined}
      onMouseLeave={showTooltip ? () => setIsHovered(false) : undefined}
    >
      {showTooltip ? (
        <>
          <span
            className="inline-flex items-center justify-center w-5 h-5 rounded-full text-base text-[var(--text-secondary)] cursor-help bg-[rgba(20,110,105,0.1)] hover:text-[#146e69] dark:hover:text-[#2dd4bf] hover:bg-[rgba(20,110,105,0.2)] dark:hover:bg-[rgba(20,184,166,0.2)] hover:scale-110 transition-all duration-200"
            title="Hover for German character shortcuts"
          >
            ℹ️
          </span>
          {isHovered && (
            <div className="absolute bottom-[calc(100%+8px)] left-0 p-3 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-lg shadow-lg z-[10000] min-w-[280px] max-w-[320px] text-[13px] leading-[1.5] whitespace-normal pointer-events-auto">
              <div className="font-semibold text-[#146e69] dark:text-[#2dd4bf] mb-2.5 pb-2 border-b border-[var(--border-color)]">
                How to type German characters ({os === 'mac' ? 'Mac' : 'Windows'}):
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-[#146e69] dark:text-[#2dd4bf] min-w-6 text-center">ä</span>
                  <span className="text-[var(--text-primary)] flex-1">{instructions.ä}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-[#146e69] dark:text-[#2dd4bf] min-w-6 text-center">ö</span>
                  <span className="text-[var(--text-primary)] flex-1">{instructions.ö}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-[#146e69] dark:text-[#2dd4bf] min-w-6 text-center">ü</span>
                  <span className="text-[var(--text-primary)] flex-1">{instructions.ü}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-[#146e69] dark:text-[#2dd4bf] min-w-6 text-center">Ä</span>
                  <span className="text-[var(--text-primary)] flex-1">{instructions.Ä}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-[#146e69] dark:text-[#2dd4bf] min-w-6 text-center">Ö</span>
                  <span className="text-[var(--text-primary)] flex-1">{instructions.Ö}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-[#146e69] dark:text-[#2dd4bf] min-w-6 text-center">Ü</span>
                  <span className="text-[var(--text-primary)] flex-1">{instructions.Ü}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-[#146e69] dark:text-[#2dd4bf] min-w-6 text-center">ß</span>
                  <span className="text-[var(--text-primary)] flex-1">{instructions.ß}</span>
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default UmlautHelper;

